import InteractionMetricSummaryValue from "components-for/display/comparison-table/interaction-metric-summary-value";
import { Cell, Column, Heading, NumericValue, Table, TableColumns } from "@edgetier/client-components";
import { FunctionComponent, memo } from "react";
import { ILiveSummaryData, IProps } from "./live-summary-table.types";
import { InteractionMetric } from "@edgetier/watchtower-types";
import { SHOW_BLOCKED } from "./live-summary-table.constants";
import { useLiveSummaryData } from "./live-summary-table.utilities";
import { useSummaryTagGroup } from "../live-summary-tab.utilities";
import "./live-summary-table.scss";

/**
 * Display live interactions data for a tag group as a table
 */
const LiveSummaryTable: FunctionComponent<IProps> = () => {
    const [tagGroupSelectedId] = useSummaryTagGroup(undefined);
    const { isLoading, liveSummaryData } = useLiveSummaryData();

    return (
        <Table<ILiveSummaryData>
            getRowKey={({ tagId }) => tagId}
            headerIsSticky
            rows={isLoading && typeof tagGroupSelectedId !== "undefined" ? null : liveSummaryData}
            isInBox
            hasHorizontalScroll
            emptyMessage={typeof tagGroupSelectedId === "undefined" ? "Please choose a tag group" : "No results found"}
        >
            <TableColumns>
                <Column>
                    <Heading sortProperty="tag">Tag</Heading>
                    <Cell<ILiveSummaryData>>{({ tag }) => `${tag}`}</Cell>
                </Column>
                {SHOW_BLOCKED && (
                    <Column>
                        <Heading sortProperty="totalConversationsOpen">Total Open Conversations</Heading>
                        <Cell<ILiveSummaryData>>
                            {({ totalConversationsOpen }) => <NumericValue value={totalConversationsOpen} />}
                        </Cell>
                    </Column>
                )}
                <Column>
                    <Heading sortProperty="activeInteractionsCount">Active</Heading>
                    <Cell<ILiveSummaryData>>
                        {({ activeInteractionsCount }) => <NumericValue value={activeInteractionsCount} />}
                    </Cell>
                </Column>
                {SHOW_BLOCKED && (
                    <Column>
                        <Heading sortProperty="snoozedInteractionsCount">Snoozed</Heading>
                        <Cell<ILiveSummaryData>>
                            {({ snoozedInteractionsCount }) => <NumericValue value={snoozedInteractionsCount} />}
                        </Cell>
                    </Column>
                )}
                <Column>
                    <Heading sortProperty="queuingInteractionsCount">Queuing</Heading>
                    <Cell<ILiveSummaryData>>
                        {({ queuingInteractionsCount }) => <NumericValue value={queuingInteractionsCount} />}
                    </Cell>
                </Column>
                <Column>
                    <Heading sortProperty="maximumDuration">Maximum Duration</Heading>
                    <Cell<ILiveSummaryData>>
                        {({ maximumDuration }) => (
                            <InteractionMetricSummaryValue
                                value={maximumDuration}
                                interactionMetric={InteractionMetric.HandlingTime}
                                isMaybeLoading={false}
                            />
                        )}
                    </Cell>
                </Column>
                <Column>
                    <Heading sortProperty="averageQueueTime">Average Queue Time</Heading>
                    <Cell<ILiveSummaryData>>
                        {({ averageQueueTime }) => (
                            <InteractionMetricSummaryValue
                                value={averageQueueTime}
                                interactionMetric={InteractionMetric.HandlingTime}
                                isMaybeLoading={false}
                            />
                        )}
                    </Cell>
                </Column>
                <Column>
                    <Heading sortProperty="maximumQueueTime">Maximum Queue Time</Heading>
                    <Cell<ILiveSummaryData>>
                        {({ maximumQueueTime }) => (
                            <InteractionMetricSummaryValue
                                value={maximumQueueTime}
                                interactionMetric={InteractionMetric.HandlingTime}
                                isMaybeLoading={false}
                            />
                        )}
                    </Cell>
                </Column>
                <Column>
                    <Heading sortProperty="serviceLevelAgreement">Service Level Agreement</Heading>
                    <Cell<ILiveSummaryData>>
                        {({ serviceLevelAgreement }) => (
                            <InteractionMetricSummaryValue
                                value={typeof serviceLevelAgreement === "number" ? serviceLevelAgreement : 0}
                                interactionMetric={InteractionMetric.ServiceLevelAgreement}
                                isMaybeLoading={false}
                            />
                        )}
                    </Cell>
                </Column>
                <Column>
                    <Heading sortProperty="availableAgents">Available Agents</Heading>
                    <Cell<ILiveSummaryData>>{({ availableAgents }) => <NumericValue value={availableAgents} />}</Cell>
                </Column>
                <Column>
                    <Heading sortProperty="unavailableAgents">Unavailable Agents</Heading>
                    <Cell<ILiveSummaryData>>
                        {({ unavailableAgents }) => <NumericValue value={unavailableAgents} />}
                    </Cell>
                </Column>
                <Column>
                    <Heading sortProperty="recentlyOfflineAgents">Recently Offline Agents</Heading>
                    <Cell<ILiveSummaryData>>
                        {({ recentlyOfflineAgents }) => <NumericValue value={recentlyOfflineAgents} />}
                    </Cell>
                </Column>
                <Column>
                    <Heading sortProperty="busiestAgentsConcurrency">Busiest Agent's Concurrency</Heading>
                    <Cell<ILiveSummaryData>>
                        {({ busiestAgentsConcurrency }) => <NumericValue value={busiestAgentsConcurrency} />}
                    </Cell>
                </Column>
                <Column>
                    <Heading sortProperty="averageAgentsConcurrency">Agent Average Concurrency</Heading>
                    <Cell<ILiveSummaryData>>
                        {({ averageAgentsConcurrency }) => (
                            <NumericValue value={averageAgentsConcurrency} options={{ maximumFractionDigits: 1 }} />
                        )}
                    </Cell>
                </Column>
            </TableColumns>
        </Table>
    );
};

export default memo(LiveSummaryTable);
